import { ApolloError } from '@apollo/client';
import {
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Link,
} from '@apollo/orbit';
import React, { useState } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import Config from 'src/lib/config';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { SharedCheckConfigCardProps } from '../common/SharedCheckConfigCardProps';

import { OperationChecksModal } from './OperationChecksModal';

type Props = Omit<SharedCheckConfigCardProps, 'graphCheckConfig'>;

export const OperationsCheckCard = ({
  graphRef,
  variantCheckConfig,
  roles,
}: Props) => {
  const location = useLocation();
  const [updateConfigError, setUpdateConfigError] =
    useState<ApolloError | null>(null);

  const { operationsChecksEnabled } = variantCheckConfig;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            Run operation checks: {operationsChecksEnabled ? 'ON' : 'OFF'}
          </CardTitle>
          <p className="text-neutral">Recommended: On</p>
          <CardDescription>
            Operation checks compare your proposed schema changes against
            historical operations to verify whether the changes will break any
            of your graph's active clients.{' '}
            <Link
              isExternal
              to="https://www.apollographql.com/docs/graphos/delivery/schema-checks/#types-of-checks"
            >
              Learn more about operation checks
            </Link>
          </CardDescription>
          <Button
            variant="secondary"
            isDisabled={!roles.canWriteCheckConfiguration}
            as={ReactRouterLink}
            to={setOverlayVisibleLocation({
              location,
              overlayName: Config.modals.operationChecksSettings,
            })}
          >
            Change
          </Button>
        </CardHeader>
        {/* Form update error message */}
        {updateConfigError && (
          <CardBody>
            <ErrorMessage error={updateConfigError} />
          </CardBody>
        )}
      </Card>

      <LazyUrlModal name={Config.modals.operationChecksSettings}>
        {({ closeModal, isOpen }) => (
          <OperationChecksModal
            isOpen={isOpen}
            onClose={closeModal}
            graphRef={graphRef}
            operationsChecksEnabled={operationsChecksEnabled}
            roles={roles}
            setUpdateConfigError={setUpdateConfigError}
          />
        )}
      </LazyUrlModal>
    </>
  );
};
